import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import FileUpload from '../components/FileUpload'
import MaterialSelector from '../components/MaterialSelector'
import LoadConfigurator from '../components/LoadConfigurator'
import BoundaryConfigurator from '../components/BoundaryConfigurator'
import StressViewer from '../components/StressViewer'
import ReportPanel from '../components/ReportPanel'

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || ''}/api/fea/standalone`
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.osteo3d.com'

// Get auth token from URL param or sessionStorage
function getToken() {
  const params = new URLSearchParams(window.location.search)
  const urlToken = params.get('token')
  if (urlToken) {
    sessionStorage.setItem('feaToken', urlToken)
    // Clean URL
    window.history.replaceState({}, '', window.location.pathname)
    return urlToken
  }
  return sessionStorage.getItem('feaToken')
}

function authHeaders() {
  const token = sessionStorage.getItem('feaToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function getVerdict(sf) {
  if (sf >= 3) return { label: 'Safe', color: 'text-green-400', barColor: 'border-green-800/60', plain: 'Your part can comfortably handle this load.' }
  if (sf >= 1.5) return { label: 'Adequate', color: 'text-blue-400', barColor: 'border-blue-800/60', plain: 'Part holds, but margin is moderate.' }
  if (sf >= 1) return { label: 'Marginal', color: 'text-yellow-400', barColor: 'border-yellow-800/60', plain: 'Barely survives. Consider redesigning.' }
  return { label: 'Will Fail', color: 'text-red-400', barColor: 'border-red-800/60', plain: 'Stress exceeds material strength. Part will break.' }
}

export default function FEAPage() {
  const [authState, setAuthState] = useState('checking') // checking | ok | denied
  const [authEmail, setAuthEmail] = useState(null)
  const [stlFile, setStlFile] = useState(null)
  const [materials, setMaterials] = useState({})
  const [loadPresets, setLoadPresets] = useState([])
  const [selectedMaterial, setSelectedMaterial] = useState('ti6al4v')
  const [loadVector, setLoadVector] = useState([0, -1000, 0])
  const [fixedRegions, setFixedRegions] = useState([
    { type: 'extremes', axis: 'y', end: 'min', percent: 5 }
  ])
  const [loadRegions, setLoadRegions] = useState([
    { type: 'extremes', axis: 'y', end: 'max', percent: 5 }
  ])
  const [meshSizeFactor, setMeshSizeFactor] = useState(1.0)
  const [mesher, setMesher] = useState('ftetwild') // 'ftetwild' (Medical) or 'gmsh' (CAD)

  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [isRunning, setIsRunning] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(null)
  const [nodeData, setNodeData] = useState(null)
  const [showReport, setShowReport] = useState(false)
  const [showStressTest, setShowStressTest] = useState(false)

  // Auth check on load
  useEffect(() => {
    const token = getToken()
    if (!token) {
      setAuthState('denied')
      return
    }
    axios.get(`${API_BASE}/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setAuthState('ok')
        setAuthEmail(res.data.email)
      })
      .catch(() => {
        sessionStorage.removeItem('feaToken')
        setAuthState('denied')
      })
  }, [])

  // Load materials after auth
  useEffect(() => {
    if (authState !== 'ok') return
    axios.get(`${API_BASE}/materials`, { headers: authHeaders() }).then(res => {
      setMaterials(res.data.materials)
      setLoadPresets(res.data.loadPresets)
    }).catch(() => {})
  }, [authState])

  useEffect(() => {
    if (!jobId || !isRunning) return
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/status/${jobId}`, { headers: authHeaders() })
        setJobStatus(res.data)
        if (res.data.status === 'complete') {
          setIsRunning(false)
          setResults(res.data.summary)
          const nodeRes = await axios.get(`${API_BASE}/results/${jobId}/nodes`, { headers: authHeaders() })
          setNodeData(nodeRes.data)
        } else if (res.data.status === 'failed') {
          setIsRunning(false)
          setError(res.data.error)
        }
      } catch { /* keep polling */ }
    }, 3000)
    return () => clearInterval(interval)
  }, [jobId, isRunning])

  const startSimulation = useCallback(async () => {
    if (!stlFile) return
    setError(null)
    setResults(null)
    setNodeData(null)
    setJobStatus(null)
    setIsUploading(true)
    setShowReport(false)
    setShowStressTest(false)

    const formData = new FormData()
    formData.append('file', stlFile)
    formData.append('materialId', selectedMaterial)
    formData.append('fixedRegions', JSON.stringify(fixedRegions))
    formData.append('loadRegions', JSON.stringify(loadRegions))
    formData.append('loadVector', JSON.stringify(loadVector))
    formData.append('meshSizeFactor', meshSizeFactor.toString())
    formData.append('mesher', mesher)

    try {
      const res = await axios.post(`${API_BASE}/start`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() },
        timeout: 600000,
      })
      setJobId(res.data.jobId)
      setIsRunning(true)
      setIsUploading(false)
      setJobStatus({ status: 'uploading', progress: 10, phase: 'Uploading model to server...' })
    } catch (err) {
      setIsUploading(false)
      setError(err.response?.data?.error || err.message)
    }
  }, [stlFile, selectedMaterial, fixedRegions, loadRegions, loadVector, meshSizeFactor, mesher])

  const busy = isRunning || isUploading
  const canRun = stlFile && !busy
  const sf = results?.results?.safety_factor
  const verdict = sf != null ? getVerdict(sf) : null

  // Auth gate
  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Spinner />
          <p className="text-gray-500 text-sm mt-3">Verifying access...</p>
        </div>
      </div>
    )
  }

  if (authState === 'denied') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-sm">
          <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-12 mx-auto mb-6" />
          <h2 className="text-xl font-bold text-white mb-2">Access Required</h2>
          <p className="text-gray-400 text-sm mb-6">
            Osteo3d FEA is available to authorised team members only. Please sign in via the Admin Portal to access this tool.
          </p>
          <a
            href={`${PORTAL_URL}/admin/login`}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Sign in via Admin Portal
          </a>
          <p className="text-gray-600 text-xs mt-4">
            Contact your administrator if you need access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <header className="border-b border-gray-800 px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
            <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-8 w-auto object-contain" />
          </a>
          <span className="text-[10px] text-gray-600 border border-gray-800 rounded px-1.5 py-0.5">FEA &middot; Structural Analysis</span>
        </div>
        <div className="flex items-center gap-3">
          {authEmail && <span className="text-[10px] text-gray-600">{authEmail}</span>}
          <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer"
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors">osteo3d.com</a>
        </div>
      </header>

      <div className="flex h-[calc(100vh-53px)]">
        {/* Left panel — Setup */}
        <div className="w-[380px] border-r border-gray-800 overflow-y-auto p-4 space-y-4 flex-shrink-0">

          {/* Upload */}
          <div>
            <StepLabel number={1} label="Upload Model" done={!!stlFile} />
            <FileUpload onFileSelect={setStlFile} currentFile={stlFile} disabled={busy} />
            {!stlFile && (
              <Tip text="Upload an STL file from your CAD or slicer software to begin." />
            )}
          </div>

          {/* Configure (shown after upload) */}
          {stlFile && (
            <div className="space-y-4">
              <StepLabel number={2} label="Setup" done={!!results} />

              {/* Model Type selector */}
              <div className="bg-gray-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-300 mb-1">Model Type</h3>
                <p className="text-[11px] text-gray-500 mb-3">What kind of STL file is this?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMesher('ftetwild')}
                    className={`rounded-lg p-3 text-left border transition-colors ${
                      mesher === 'ftetwild'
                        ? 'bg-blue-600/20 border-blue-600 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <p className="text-xs font-semibold">Medical / CT Scan</p>
                    <p className="text-[10px] mt-0.5 opacity-70">Scanned anatomy, implants</p>
                  </button>
                  <button
                    onClick={() => setMesher('gmsh')}
                    className={`rounded-lg p-3 text-left border transition-colors ${
                      mesher === 'gmsh'
                        ? 'bg-blue-600/20 border-blue-600 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <p className="text-xs font-semibold">CAD / Designed Part</p>
                    <p className="text-[10px] mt-0.5 opacity-70">Cutting guides, brackets</p>
                  </button>
                </div>
                <Tip text="Medical models from CT/CBCT scans use a specialized engine that handles complex organic geometry. CAD parts use a faster engine optimized for clean engineered shapes." />
              </div>

              <div>
                <MaterialSelector materials={materials} selected={selectedMaterial} onChange={setSelectedMaterial} />
                <Tip text="Choose the material your part will be made from. This affects stiffness and strength." />
              </div>

              <div>
                <LoadConfigurator presets={loadPresets} loadVector={loadVector} onChange={setLoadVector} />
                <Tip text="Select a preset or enter custom force. 1 kg ~ 10 N." />
              </div>

              <div>
                <BoundaryConfigurator
                  fixedRegions={fixedRegions} loadRegions={loadRegions}
                  onFixedChange={setFixedRegions} onLoadChange={setLoadRegions}
                />
                <Tip text="Fixed = where held in place. Load = where force is applied." />
              </div>

              {/* Results summary in sidebar */}
              {results && verdict && (
                <div>
                  <StepLabel number={3} label="Results" done />

                  {/* Verdict card */}
                  <div className={`rounded-xl border-2 p-4 mb-3 ${verdict.barColor} bg-gray-900/80`}>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Verdict</p>
                      <p className={`text-2xl font-black ${verdict.color}`}>{verdict.label}</p>
                      <p className="text-3xl font-black text-white mt-1">
                        {sf >= 9999 ? 'N/A' : sf.toFixed(2)}
                      </p>
                      <p className="text-[10px] text-gray-500">Safety Factor</p>
                      <p className="text-xs text-gray-400 mt-2">{verdict.plain}</p>
                    </div>
                  </div>

                  {/* Quick metrics */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <QuickMetric label="Max Stress" value={`${results.results.max_von_mises_mpa} MPa`} />
                    <QuickMetric label="Max Displacement" value={`${results.results.max_displacement_mm} mm`} />
                    <QuickMetric label="Elements" value={results.mesh.num_elements?.toLocaleString()} />
                    <QuickMetric label="Nodes" value={results.mesh.num_nodes?.toLocaleString()} />
                  </div>

                  {/* Auto Stress Test */}
                  {results.results.max_von_mises_mpa > 0 && (() => {
                    const totalForce = results.boundary_conditions?.total_force_n || Math.sqrt(loadVector.reduce((s, v) => s + v * v, 0))
                    const maxMpa = results.results.max_von_mises_mpa
                    const yieldMpa = results.material.yield_strength / 1e6
                    const stressPerN = maxMpa / totalForce
                    const yieldLoad = yieldMpa / stressPerN
                    const safeLoad = yieldLoad / 3
                    const dispPerN = results.results.max_displacement_mm / totalForce

                    const sweepMultipliers = [0.1, 0.25, 0.5, 0.75, 1.0, 1.5, 2.0, 3.0, 5.0]
                    const sweepRows = sweepMultipliers
                      .map(mult => ({
                        load: totalForce * mult,
                        stress: maxMpa * mult,
                        disp: results.results.max_displacement_mm * mult,
                        sf: yieldMpa / (maxMpa * mult),
                        isApplied: mult === 1.0,
                      }))
                      .filter(r => r.load <= yieldLoad * 1.5)

                    return (
                      <div className="mb-3">
                        <button
                          onClick={() => setShowStressTest(!showStressTest)}
                          className={`w-full rounded-xl border p-3 text-left transition-all ${
                            showStressTest
                              ? 'bg-purple-950/30 border-purple-700/50'
                              : 'bg-gray-800/80 border-gray-700 hover:border-purple-700/50 hover:bg-purple-950/20'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span className="text-sm font-semibold text-purple-300">Auto Stress Test</span>
                            </div>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showStressTest ? 'rotate-180' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-1">Find out at what load your part breaks — no re-run needed</p>
                        </button>

                        {showStressTest && (
                          <div className="mt-2 space-y-3 animate-in">
                            {/* Hero: Safe vs Breaking */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-green-950/20 border border-green-800/50 rounded-lg p-3 text-center">
                                <p className="text-[9px] text-gray-500 uppercase tracking-wider">Safe up to (SF 3)</p>
                                <p className="text-lg font-black text-green-400 mt-0.5">{safeLoad.toFixed(0)} N</p>
                                <p className="text-[10px] text-gray-500">{(safeLoad / 9.81).toFixed(1)} kg</p>
                              </div>
                              <div className="bg-red-950/20 border border-red-800/50 rounded-lg p-3 text-center">
                                <p className="text-[9px] text-gray-500 uppercase tracking-wider">Breaks at (SF 1)</p>
                                <p className="text-lg font-black text-red-400 mt-0.5">{yieldLoad.toFixed(0)} N</p>
                                <p className="text-[10px] text-gray-500">{(yieldLoad / 9.81).toFixed(1)} kg</p>
                              </div>
                            </div>

                            {/* Visual capacity bar */}
                            <div className="bg-gray-900/80 border border-gray-800 rounded-lg p-3">
                              <div className="relative h-5 bg-gray-800 rounded-full overflow-hidden">
                                <div className="absolute inset-y-0 left-0 bg-green-800/50 rounded-l-full"
                                  style={{ width: `${Math.min(100, (safeLoad / (yieldLoad * 1.2)) * 100)}%` }} />
                                <div className="absolute inset-y-0 bg-yellow-800/40"
                                  style={{ left: `${(safeLoad / (yieldLoad * 1.2)) * 100}%`, width: `${((yieldLoad - safeLoad) / (yieldLoad * 1.2)) * 100}%` }} />
                                <div className="absolute inset-y-0 right-0 bg-red-800/40 rounded-r-full"
                                  style={{ left: `${Math.min(100, (yieldLoad / (yieldLoad * 1.2)) * 100)}%` }} />
                                {/* Your load marker */}
                                <div className="absolute inset-y-0 w-0.5 bg-white shadow-lg"
                                  style={{ left: `${Math.min(98, (totalForce / (yieldLoad * 1.2)) * 100)}%` }}>
                                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] text-white font-bold whitespace-nowrap bg-gray-900 px-1 rounded">
                                    You
                                  </div>
                                </div>
                              </div>
                              <div className="flex justify-between mt-1.5 text-[8px] text-gray-600">
                                <span>0</span>
                                <span className="text-green-600">{safeLoad.toFixed(0)} N</span>
                                <span className="text-red-600">{yieldLoad.toFixed(0)} N</span>
                              </div>
                            </div>

                            {/* Sweep table */}
                            <div className="bg-gray-900/80 border border-gray-800 rounded-lg overflow-hidden">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="border-b border-gray-800 text-gray-500">
                                    <th className="text-left p-2 font-medium">Load</th>
                                    <th className="text-right p-2 font-medium">Stress</th>
                                    <th className="text-right p-2 font-medium">SF</th>
                                    <th className="text-right p-2 font-medium">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sweepRows.map((row, i) => {
                                    const sfCol = row.sf >= 3 ? 'text-green-400' : row.sf >= 1.5 ? 'text-blue-400' : row.sf >= 1 ? 'text-yellow-400' : 'text-red-400'
                                    const sfLbl = row.sf >= 3 ? 'Safe' : row.sf >= 1.5 ? 'OK' : row.sf >= 1 ? 'Risky' : 'FAIL'
                                    return (
                                      <tr key={i} className={`border-b border-gray-800/50 ${row.isApplied ? 'bg-blue-950/30' : ''}`}>
                                        <td className="p-2">
                                          <span className="text-gray-200 font-mono">{row.load.toFixed(0)} N</span>
                                          <span className="text-gray-600 ml-1">({(row.load / 9.81).toFixed(1)} kg)</span>
                                          {row.isApplied && <span className="text-blue-400 text-[8px] ml-1 font-bold">YOU</span>}
                                        </td>
                                        <td className="p-2 text-right font-mono text-gray-300">{row.stress.toFixed(1)}</td>
                                        <td className={`p-2 text-right font-mono font-bold ${sfCol}`}>{row.sf.toFixed(1)}</td>
                                        <td className={`p-2 text-right font-bold ${sfCol}`}>{sfLbl}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <Tip text="Stress scales linearly with force in this analysis. All values computed instantly from your single run — no waiting!" />
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* View Report button */}
                  <button
                    onClick={() => setShowReport(true)}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Full Report &amp; Export PDF
                  </button>

                  <Tip text="The report includes detailed technical analysis with plain-English explanations for every metric." />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel — Viewer + Action controls */}
        <div className="flex-1 relative">
          <StressViewer stlFile={stlFile} nodeData={nodeData} results={results} />

          {/* Floating action bar at bottom of viewer (only when file is loaded) */}
          {stlFile && (
            <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
              <div className="max-w-2xl mx-auto pointer-events-auto">

                {/* Progress overlay (during analysis) */}
                {(busy || (jobStatus && !results)) && (
                  <div className="bg-gray-900/95 backdrop-blur-md border border-gray-700 rounded-xl p-4 mb-3 shadow-2xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Spinner />
                        <span className="text-sm text-gray-300 font-medium">
                          {jobStatus?.phase || (isUploading ? 'Uploading file...' : 'Starting...')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {jobStatus?.elapsed != null && (
                          <span className="text-xs text-gray-500">{jobStatus.elapsed}s</span>
                        )}
                        <button
                          onClick={() => {
                            setIsRunning(false)
                            setIsUploading(false)
                            setJobId(null)
                            setJobStatus(null)
                            setError(null)
                          }}
                          className="text-xs text-red-400 hover:text-red-300 font-medium px-2 py-1 rounded hover:bg-red-900/30 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-blue-500 transition-all duration-700"
                        style={{ width: `${jobStatus?.progress || 5}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error overlay */}
                {error && !busy && (
                  <div className="bg-red-950/95 backdrop-blur-md border border-red-800 rounded-xl p-4 mb-3 shadow-2xl">
                    <p className="text-sm text-red-400 font-medium">Analysis failed</p>
                    <p className="text-xs text-red-400/70 mt-1 line-clamp-2">{error}</p>
                    <button onClick={() => setError(null)}
                      className="text-xs text-red-400 underline mt-2 hover:text-red-300">Dismiss</button>
                  </div>
                )}

                {/* Action bar: mesh quality + run button */}
                {!busy && !results && (
                  <div className="bg-gray-900/95 backdrop-blur-md border border-gray-700 rounded-xl p-3 shadow-2xl flex items-center gap-4">
                    {/* Mesh quality compact */}
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Mesh</span>
                      <span className="text-[10px] text-gray-500">Fine</span>
                      <input
                        type="range" min="0.3" max="3.0" step="0.1"
                        value={meshSizeFactor}
                        onChange={e => setMeshSizeFactor(parseFloat(e.target.value))}
                        className="flex-1 h-1 accent-blue-500"
                      />
                      <span className="text-[10px] text-gray-500">Coarse</span>
                    </div>

                    {/* Run button */}
                    <button
                      onClick={startSimulation}
                      disabled={!canRun}
                      className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap flex items-center gap-2 ${
                        canRun
                          ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 active:scale-[0.97]'
                          : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" />
                      </svg>
                      Run Analysis
                    </button>
                  </div>
                )}

                {/* Completed: verdict bar with View Report */}
                {results && verdict && !busy && (
                  <div className={`bg-gray-900/95 backdrop-blur-md border-2 ${verdict.barColor} rounded-xl p-3 shadow-2xl`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Verdict icon */}
                        {sf >= 1 ? (
                          <svg className={`w-6 h-6 ${verdict.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${verdict.color}`}>{verdict.label}</span>
                            <span className="text-xs text-gray-500">|</span>
                            <span className="text-sm text-white font-bold">SF {sf >= 9999 ? 'N/A' : sf.toFixed(2)}</span>
                            <span className="text-xs text-gray-500">|</span>
                            <span className="text-xs text-gray-400">{results.results.max_von_mises_mpa} MPa peak</span>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5">{verdict.plain}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowReport(true)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors flex items-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Report
                        </button>
                        <button
                          onClick={() => { setResults(null); setNodeData(null); setJobStatus(null); setError(null); setShowReport(false) }}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                        >
                          Re-run
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full-screen Report overlay */}
      {showReport && (
        <ReportPanel
          summary={results}
          materials={materials}
          selectedMaterial={selectedMaterial}
          stlFile={stlFile}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  )
}

function StepLabel({ number, label, done }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
        done ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'
      }`}>
        {done ? (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : number}
      </div>
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
  )
}

function Tip({ text }) {
  return (
    <p className="text-[11px] text-gray-500 mt-2 leading-relaxed pl-1 border-l-2 border-gray-800 ml-1">{text}</p>
  )
}

function QuickMetric({ label, value }) {
  return (
    <div className="bg-gray-800/80 rounded-lg p-2">
      <p className="text-[10px] text-gray-500">{label}</p>
      <p className="text-xs text-white font-semibold">{value}</p>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin text-blue-400" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
        strokeDasharray="31.4 31.4" strokeDashoffset="10" />
    </svg>
  )
}
